/**
 * @module Logging
 * @summary The key function to log errors and key events to our dashboard will be here
 * @see LogError
 * @see LogInfo
 */
import { TrackJS } from './trackjs-isomorphic';
import isBrowser from './isBrowser';
import { getNewRelicAgent } from './logging/getNewRelicAgent';
import {
  hasActiveExperiment,
  getSerializedExperimentsInfo,
} from './ab-testing/getActiveTestingVariant';

const IS_DEBUG_ENABLED = false;

/**
 * @summary Set the user ID for tracking
 * @param {Object} sessionData - sessionId, schoolCode, googleTagManagerSiteId, floodLightActivityFilters, isPersonalized, geoLocation, developmentEnvironment
 */
let sessionDataForLogging = {};

let logOrder = 0;
function getLogOrder() {
  if (!isBrowser()) {
    return 0;
  }

  try {
    const storedLogOrder = sessionStorage.getItem('newRelicLogOrder') || 0;
    sessionStorage.setItem(
      'newRelicLogOrder',
      parseInt(storedLogOrder, 10) + 1
    );
    return storedLogOrder;
  } catch (__error) {
    logOrder += 1;
    return logOrder;
  }
}

/**
 * Logs a message to New Relic with the specified level and custom attributes.
 *
 * @param {string} message - The message to log.
 * @param {string} level - The log level.
 * @param {object} meta - Custom attributes to include with the log message.
 * @return {void}
 */
export function newRelicLogger(message, level, meta = {}) {
  const metaData = {
    description: meta.description,
    endpoint: meta.endpoint,
    'tms.app': 'microsite-platform',
    'entity.id': sessionDataForLogging.sessionId,
    environment: sessionDataForLogging.developmentEnvironment,
    schoolCode: sessionDataForLogging.schoolCode,
    sid: sessionDataForLogging.sid,
    siteType: sessionDataForLogging.siteType,
    applicationBuildId: sessionDataForLogging.environmentVariables?.buildId,
    triadBackendDomain:
      sessionDataForLogging.environmentVariables?.triadBackendDomain,
    gclid: sessionDataForLogging.searchEngineCookies?.gclid,
    msclkid: sessionDataForLogging.searchEngineCookies?.msclkid,
    priority: meta.priority,
  };

  if (isBrowser()) {
    metaData.userAgent = window.navigator?.userAgent;
    metaData.fullUrl = window.location?.href;
    metaData.scrollX = window.scrollX;
    metaData.scrollY = window.scrollY;
    metaData.windowTotalWidth = window.innerWidth;
    metaData.windowTotalHeight = window.innerHeight;

    if (hasActiveExperiment()) {
      metaData.experiments = getSerializedExperimentsInfo();
    }

    if (meta.hashedUserInfo) {
      Object.assign(metaData, meta.hashedUserInfo);
    }

    getNewRelicAgent().then((agent) => {
      metaData.logOrder = getLogOrder();
      if (agent && agent.log) {
        agent.log(message, {
          level,
          customAttributes: metaData,
        });
      }
    });
  }

  if (!isBrowser()) {
    // TODO [FSIU-65]: Add support for server logging to new relic
  }
}

/**
 * @summary Get the session data used for logging
 * @returns {floodLightActivityFilters: {
 *   trackingSchoolCode: string,
 *   propertyOrigin: string,
 *   isClickUser: boolean,
 *   fvCampaignType: string,
 *   adPlatformSource: string
 * }}
 */
// TODO: [FSIU-143] This has gotten too hacky. We need to refactor this to be more manageable.
// All logging and analytics functions need to be in some module that has access to global context.
export function getSessionDataForLogging() {
  return sessionDataForLogging;
}

export function configureSessionForLogging(sessionData = {}) {
  if (sessionData.sessionId && TrackJS.isInstalled()) {
    if (isBrowser()) {
      TrackJS.addMetadata('sessionId', sessionData.sessionId);
    } else {
      TrackJS.addLogTelemetry(
        'info',
        `addMeta ran sessionId=${sessionData.sessionId}`,
        {
          sessionId: sessionData.sessionId,
        }
      );
    }
  }

  getNewRelicAgent().then((agent) => {
    if (agent && agent.setUserId && sessionData.sessionId) {
      agent.setUserId(sessionData.sessionId);
    }
    Object.keys(sessionData).forEach((key) => {
      if (agent && agent.setCustomAttribute) {
        agent.setCustomAttribute(key, sessionData[key]);
      }
    });
  });

  sessionDataForLogging = { ...sessionDataForLogging, ...sessionData };
}

/**
 * @summary Track a page view
 * @deprecated use Log Function instead
 * @param {Object} attributes - The attributes to track with the page view
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/using-browser-apis/
 */
export function newrelicPageAction(actionName, attributes = {}) {
  try {
    let gtmDataLayer;

    try {
      gtmDataLayer =
        window.google_tag_manager[sessionDataForLogging.googleTagManagerSiteId]
          .dataLayer;
    } catch (error) {
      gtmDataLayer = { get: () => {} };
    }

    const filterValues = sessionDataForLogging.floodLightActivityFilters || {};

    const globalAttributes = {
      ...attributes,
      gtmOriginSchoolCode: gtmDataLayer.get('origin_school_code'),
      fvTrackingSchoolCode: filterValues.trackingSchoolCode,
      fvPropertyOrigin: filterValues.propertyOrigin,
      fvIsClickUser: filterValues.isClickUser,
      fvCampaignType: filterValues.campaignType,
      fvAdPlatformSource: filterValues.adPlatformSource,
      schoolCode: sessionDataForLogging.schoolCode,
      developmentEnvironment: sessionDataForLogging.developmentEnvironment,
      gclid: sessionDataForLogging.searchEngineCookies?.gclid,
      msclkid: sessionDataForLogging.searchEngineCookies?.msclkid,
      sid: sessionDataForLogging.sid,
    };
    getNewRelicAgent().then((agent) => {
      if (agent && agent.addPageAction) {
        agent.addPageAction(actionName, globalAttributes);
      }
    });
  } catch (error) {
    console.error('newrelicPageAction', error);
  }
}

/**
 * Logs a warning message with custom attributes.
 *
 * @param {string} message - The warning message to log.
 * @param {object} meta - Custom attributes to include with the log message.
 * @return {void}
 */
export function LogWarning(message, meta = {}) {
  newRelicLogger(message, 'warn', meta);
}

/**
 * Logs a debug message with custom attributes.
 *
 * @param {string} message - The debug message to log.
 * @param {object} meta - Custom attributes to include with the log message.
 * @return {void}
 */
export function LogDebug(message, meta = {}) {
  if (IS_DEBUG_ENABLED) {
    newRelicLogger(message, 'debug', meta);
  }
}

/**
 * @summary This is only to be used for logging information, such as key events.
 * @param {String} message - The message to record
 * @param {Object} meta - extra key value pairs to record with this log
 */
export function LogInfo(message, meta = {}) {
  newRelicLogger(message, 'info', meta);
  if (TrackJS.isInstalled()) {
    if (isBrowser()) {
      window.console.info(`${message}. metaData=${JSON.stringify(meta)}`);
    } else {
      TrackJS.addLogTelemetry('info', message, meta);
    }
  }
}
